<div class="course-page">
  <div class="page-header">
    <div>
      <p-button
        label="Back to Course"
        icon="pi pi-arrow-left"
        [text]="true"
        [routerLink]="['/practice-courses', courseVersionId]"
      />
      <h1>{{ detail()?.user?.name ?? 'Rep progress' }}</h1>
      @if (detail(); as data) {
        <p>{{ data.user.email }} · {{ data.course.name }} v{{ data.course.version }}</p>
      }
    </div>
    @if (detail(); as data) {
      <p-tag
        [value]="label(data.enrollment.readinessState)"
        [severity]="severity(data.enrollment.readinessState)"
      />
    }
  </div>

  @if (error()) {
    <div class="error-state" role="alert">
      <span>Rep progress could not be loaded.</span
      ><p-button label="Try again" icon="pi pi-refresh" [outlined]="true" (onClick)="load()" />
    </div>
  }
  @if (loading()) {
    <p-card
      ><div class="empty"><i class="pi pi-spin pi-spinner"></i> Loading rep progress…</div></p-card
    >
  }
  @if (detail(); as data) {
    @if (data.freshness?.status === 'stale') {
      <div class="stale-state" role="status">
        <i class="pi pi-clock"></i> This progress snapshot is catching up with the latest attempt.
        @if (data.freshness?.sourceGeneratedAt) {
          <span
            >Last complete snapshot: {{ data.freshness?.sourceGeneratedAt | date: 'medium' }}.</span
          >
        }
        <p-button label="Refresh" icon="pi pi-refresh" [text]="true" (onClick)="load()" />
      </div>
    } @else if (data.freshness?.status === 'unavailable') {
      <div class="unavailable-state" role="status">
        <i class="pi pi-info-circle"></i> Aggregate freshness is unavailable — there is no snapshot
        data to compare against yet.
      </div>
    }
    <div class="metric-grid">
      <p-card
        ><span>Readiness score</span
        ><strong>{{ percent(data.progressSnapshot?.readinessScore) }}%</strong
        ><small>{{ label(data.enrollment.readinessState) }}</small></p-card
      >
      <p-card
        ><span>Attempts</span><strong>{{ data.attempts.length }}</strong
        ><small>{{
          data.attempts.length
            ? 'Latest ' + (data.attempts[0].createdAt | date: 'mediumDate')
            : 'Not started'
        }}</small></p-card
      >
      <p-card
        ><span>Current module</span
        ><strong class="compact">{{ data.enrollment.currentModule?.name || '—' }}</strong
        ><small>Enrollment {{ label(data.enrollment.status) }}</small></p-card
      >
    </div>

    <p-card header="Assignment and enrollment">
      <div class="metadata-grid">
        <div>
          <span>Assignment source</span>
          <p>
            {{ data.assignment.source
            }}{{ data.assignment.team ? ' · ' + data.assignment.team.name : '' }}
          </p>
        </div>
        <div>
          <span>Assigned</span>
          <p>{{ data.assignment.createdAt | date: 'medium' }}</p>
        </div>
        <div>
          <span>Due</span>
          <p>
            {{
              data.assignment.dueAt ? (data.assignment.dueAt | date: 'mediumDate') : 'No due date'
            }}
          </p>
        </div>
        <div>
          <span>Enrollment status</span>
          <p>{{ label(data.enrollment.status) }}</p>
        </div>
      </div>
    </p-card>

    <p-card header="Latest progress snapshot">
      @if (data.progressSnapshot; as snapshot) {
        <div class="snapshot-grid">
          <section>
            <h3>Module progress</h3>
            <p-table [value]="snapshot.moduleProgress" styleClass="p-datatable-sm">
              <ng-template pTemplate="header"
                ><tr>
                  <th>Module</th>
                  <th>Passed</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>Coverage</th>
                </tr></ng-template
              >
              <ng-template pTemplate="body" let-entry
                ><tr>
                  <td>{{ moduleName(entry.moduleId, entry.orderIndex) }}</td>
                  <td>{{ entry.passedAttempts }}</td>
                  <td>{{ entry.requiredAttempts }}</td>
                  <td>
                    <p-tag
                      [value]="entry.passed ? 'Passed' : 'In progress'"
                      [severity]="entry.passed ? 'success' : 'info'"
                    />
                  </td>
                  <td>{{ entry.scenarioCoverage.covered }} / {{ entry.scenarioCoverage.total }}</td>
                </tr></ng-template
              >
              <ng-template pTemplate="emptymessage"
                ><tr>
                  <td colspan="5" class="empty">No module progress recorded.</td>
                </tr></ng-template
              >
            </p-table>
          </section>
          <section>
            <h3>Skill mastery</h3>
            <p-table [value]="snapshot.skillProgress" styleClass="p-datatable-sm">
              <ng-template pTemplate="header"
                ><tr>
                  <th>Skill</th>
                  <th>Avg score</th>
                  <th>Threshold</th>
                  <th>Status</th>
                </tr></ng-template
              >
              <ng-template pTemplate="body" let-entry
                ><tr>
                  <td>{{ label(entry.skill) }}</td>
                  <td>{{ entry.averageScore | number: '1.1-1' }}</td>
                  <td>{{ entry.threshold | number: '1.1-1' }}</td>
                  <td>
                    <p-tag
                      [value]="entry.meetsThreshold ? 'Met' : 'Below'"
                      [severity]="entry.meetsThreshold ? 'success' : 'danger'"
                    />
                  </td></tr
              ></ng-template>
              <ng-template pTemplate="emptymessage"
                ><tr>
                  <td colspan="4" class="empty">No skill mastery recorded.</td>
                </tr></ng-template
              >
            </p-table>
          </section>
          <section>
            <h3>Scenario coverage</h3>
            <p-table [value]="snapshot.scenarioProgress" styleClass="p-datatable-sm">
              <ng-template pTemplate="header"
                ><tr>
                  <th>Scenario range</th>
                  <th>Completed</th>
                  <th>Passed</th>
                  <th>Last attempt</th>
                </tr></ng-template
              >
              <ng-template pTemplate="body" let-entry
                ><tr>
                  <td>{{ scenarioName(entry.scenarioRangeId) }}</td>
                  <td>{{ entry.completedAttempts }}</td>
                  <td>{{ entry.passedAttempts }}</td>
                  <td>
                    {{ entry.lastAttemptAt ? (entry.lastAttemptAt | date: 'mediumDate') : '—' }}
                  </td>
                </tr></ng-template
              >
              <ng-template pTemplate="emptymessage"
                ><tr>
                  <td colspan="4" class="empty">No scenario coverage recorded.</td>
                </tr></ng-template
              >
            </p-table>
          </section>
          <section>
            <h3>Recommended next actions</h3>
            @if (snapshot.recommendedActions.length) {
              <ul>
                @for (action of snapshot.recommendedActions; track $index) {
                  <li>{{ action }}</li>
                }
              </ul>
            } @else {
              <p class="empty">No recommended actions.</p>
            }
          </section>
        </div>
      } @else {
        <div class="empty">No progress snapshot has been generated.</div>
      }
    </p-card>

    <p-card header="Attempts and linked sessions">
      <p-table [value]="data.attempts" [tableStyle]="{ 'min-width': '70rem' }">
        <ng-template pTemplate="header"
          ><tr>
            <th>Date</th>
            <th>Module</th>
            <th>Scenario</th>
            <th>Practice</th>
            <th>Difficulty</th>
            <th>Score</th>
            <th>Result</th>
            <th>Critical failures</th>
            <th>Session</th>
          </tr></ng-template
        >
        <ng-template pTemplate="body" let-attempt
          ><tr>
            <td>{{ attempt.createdAt | date: 'medium' }}</td>
            <td>{{ attempt.moduleName }}</td>
            <td>{{ attempt.scenarioRangeName }}</td>
            <td>{{ label(attempt.practiceType) }}</td>
            <td>{{ attempt.difficulty }}</td>
            <td>{{ attempt.score ?? '—' }}</td>
            <td>
              <p-tag
                [value]="attempt.passed === null ? 'Pending' : attempt.passed ? 'Passed' : 'Failed'"
                [severity]="severity(attempt.passed)"
              />
            </td>
            <td>{{ attempt.criticalFailures.join(', ') || '—' }}</td>
            <td>
              <a class="link-cell" [routerLink]="['/sessions', 'practice', attempt.sessionId]"
                >Open session <i class="pi pi-external-link"></i
              ></a>
            </td></tr
        ></ng-template>
        <ng-template pTemplate="emptymessage"
          ><tr>
            <td colspan="9" class="empty">No attempts recorded.</td>
          </tr></ng-template
        >
      </p-table>
    </p-card>
  }
</div>
